import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import FlyingRobots from '../components/FlyingRobots';
import { AlertCircle, CheckCircle2, Loader } from 'lucide-react';

export default function IntegrationSetup() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user, loading } = useAuth();
  const [setupStatus, setSetupStatus] = useState('idle'); // idle | connecting | connected | error
  const [errorMessage, setErrorMessage] = useState('');
  const [integrationId, setIntegrationId] = useState(null);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    if (!loading && !user) {
      navigate('/login');
    }
  }, [user, loading, navigate]);

  // Check if we're coming back from OAuth callback
  useEffect(() => {
    const success = searchParams.get('success');
    const id = searchParams.get('id');

    if (success === 'true' && id) {
      setSetupStatus('connected');
      setIntegrationId(id);
    }
  }, [searchParams]);

  const handleConnectMoka = async () => {
    try {
      setSetupStatus('connecting');
      setErrorMessage('');

      const response = await fetch('/api/integrations/connect', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          system_type: 'moka',
          org_id: user?.user_id || 'default_org', // Use org_id from user or default
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to initiate OAuth connection');
      }

      const data = await response.json();
      // Redirect to Moka OAuth
      window.location.href = data.authorization_url;
    } catch (error) {
      setSetupStatus('error');
      setErrorMessage(error.message || 'Connection failed');
    }
  };

  if (loading) {
    return (
      <div className="bg-[#050506] text-white min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="mb-4 text-white/60">Loading...</div>
        </div>
      </div>
    );
  }

  if (!user) {
    return null;
  }

  return (
    <div className="bg-[#050506] text-white relative min-h-screen">
      <FlyingRobots />
      <Navbar />

      <main className="pt-[100px] pb-20">
        <div className="mx-auto max-w-[600px] px-6">
          <div className="mb-12">
            <p className="eyebrow text-white/50 mb-2">SETUP</p>
            <h1 className="h-display text-[48px] md:text-[56px] leading-[1.1]">
              Connect Your POS System
            </h1>
            <p className="text-white/60 text-[16px] mt-4">
              Link your store's POS system to Grocera for real-time procurement intelligence.
            </p>
          </div>

          <div className="border border-white/10 rounded-lg bg-white/[0.02] overflow-hidden">
            {/* Moka POS Card */}
            <div className="p-8">
              <div className="flex items-start justify-between mb-6">
                <div>
                  <h2 className="h-sans text-[28px] font-medium mb-2">Moka POS</h2>
                  <p className="text-white/60 text-[14px]">
                    Real-time order and inventory sync from your Moka POS system
                  </p>
                </div>
                {setupStatus === 'connected' && integrationId && (
                  <CheckCircle2 size={24} className="text-emerald-500 flex-shrink-0 mt-1" />
                )}
              </div>

              {setupStatus === 'idle' && (
                <button
                  onClick={handleConnectMoka}
                  className="btn-primary w-full text-center"
                >
                  Connect Moka POS
                </button>
              )}

              {setupStatus === 'connecting' && (
                <div className="flex items-center justify-center gap-3 py-3 px-4 bg-white/5 rounded border border-white/10">
                  <Loader size={18} className="animate-spin text-[#10b981]" />
                  <span className="text-[14px]">Redirecting to Moka...</span>
                </div>
              )}

              {setupStatus === 'connected' && (
                <div className="flex items-center gap-3 py-3 px-4 bg-emerald-500/10 rounded border border-emerald-500/30">
                  <CheckCircle2 size={18} className="text-emerald-500 flex-shrink-0" />
                  <span className="text-[14px] text-emerald-200">
                    Connected successfully! Redirecting...
                  </span>
                </div>
              )}

              {setupStatus === 'error' && (
                <div className="space-y-4">
                  <div className="flex items-start gap-3 py-3 px-4 bg-red-500/10 rounded border border-red-500/30">
                    <AlertCircle size={18} className="text-red-500 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="text-[14px] text-red-200">{errorMessage}</p>
                    </div>
                  </div>
                  <button
                    onClick={handleConnectMoka}
                    className="btn-ghost w-full text-center text-[14px]"
                  >
                    Try Again
                  </button>
                </div>
              )}
            </div>

            {/* Info Section */}
            <div className="border-t border-white/10 p-8 bg-white/[0.005]">
              <h3 className="h-sans text-[16px] font-medium mb-4">What happens next?</h3>
              <ol className="space-y-3">
                <li className="flex gap-3">
                  <span className="text-[#10b981] font-medium text-[14px] flex-shrink-0">1.</span>
                  <span className="text-white/60 text-[14px]">
                    You'll authorize Grocera to access your Moka account
                  </span>
                </li>
                <li className="flex gap-3">
                  <span className="text-[#10b981] font-medium text-[14px] flex-shrink-0">2.</span>
                  <span className="text-white/60 text-[14px]">
                    Real-time order data will start syncing automatically
                  </span>
                </li>
                <li className="flex gap-3">
                  <span className="text-[#10b981] font-medium text-[14px] flex-shrink-0">3.</span>
                  <span className="text-white/60 text-[14px]">
                    You'll see live supplier tracking and exception alerts
                  </span>
                </li>
              </ol>
            </div>
          </div>

          <div className="mt-8 p-6 border border-white/10 rounded-lg bg-white/[0.02]">
            <p className="text-white/60 text-[13px]">
              <strong className="text-white">Need help?</strong> Check our integration docs or contact support.
              Your data is encrypted and never shared with third parties.
            </p>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}
